import { useState, useCallback, useRef } from "react";

export type Transform = {
	x: number;
	y: number;
	scale: number;
};

const INITIAL_TRANSFORM: Transform = {
	x: 0,
	y: 0,
	scale: 1,
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 10;
const ZOOM_FACTOR = 1.1;

export const useCanvasTransform = () => {
	const [transform, setTransform] = useState<Transform>(INITIAL_TRANSFORM);
	const [isPanning, setIsPanning] = useState(false);
	const lastPanPoint = useRef<{ x: number; y: number } | null>(null);

	const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
		e.preventDefault();

		const rect = e.currentTarget.getBoundingClientRect();
		const mouseX = e.clientX - rect.left;
		const mouseY = e.clientY - rect.top;

		setTransform((prev) => {
			const delta = e.deltaY > 0 ? 1 / ZOOM_FACTOR : ZOOM_FACTOR;
			const newScale = Math.max(
				MIN_SCALE,
				Math.min(MAX_SCALE, prev.scale * delta),
			);

			if (newScale === prev.scale) return prev;

			const scaleDiff = newScale - prev.scale;
			const newX = prev.x - (mouseX - prev.x) * (scaleDiff / prev.scale);
			const newY = prev.y - (mouseY - prev.y) * (scaleDiff / prev.scale);

			return {
				x: newX,
				y: newY,
				scale: newScale,
			};
		});
	}, []);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		if (e.button === 1 || (e.button === 0 && (e.altKey || e.metaKey))) {
			e.preventDefault();
			setIsPanning(true);
			lastPanPoint.current = { x: e.clientX, y: e.clientY };
		}
	}, []);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (isPanning && lastPanPoint.current) {
				e.preventDefault();
				const deltaX = e.clientX - lastPanPoint.current.x;
				const deltaY = e.clientY - lastPanPoint.current.y;

				setTransform((prev) => ({
					...prev,
					x: prev.x + deltaX,
					y: prev.y + deltaY,
				}));

				lastPanPoint.current = { x: e.clientX, y: e.clientY };
			}
		},
		[isPanning],
	);

	const handleMouseUp = useCallback(() => {
		setIsPanning(false);
		lastPanPoint.current = null;
	}, []);

	const resetTransform = useCallback(() => {
		setTransform(INITIAL_TRANSFORM);
	}, []);

	const transformStyle = {
		transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
		transformOrigin: "0 0",
	};

	const transformToCanvasCoords = useCallback(
		(clientX: number, clientY: number, rect: DOMRect) => {
			const canvasX = (clientX - rect.left - transform.x) / transform.scale;
			const canvasY = (clientY - rect.top - transform.y) / transform.scale;
			return { x: canvasX, y: canvasY };
		},
		[transform],
	);

	return {
		transform,
		transformStyle,
		isPanning,
		handleWheel,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		resetTransform,
		transformToCanvasCoords,
	};
};
